// Flight results page (port of FlightResultsPage.tsx). Client-fetches the API.
import { searchFlights } from '../lib/api.js';
import { destinationSlug } from '../lib/flightFormat.js';
import { renderFlightCard } from '../lib/flight-card.js';
// import { createAlertButton } from '../lib/create-alert.js';
import { renderDealsCardHTML, renderMoreRoutesCard } from '../lib/fr-deals-card.js';
import { hideFlightSearchLoading, showFlightSearchLoading } from '../lib/flight-search-loading.js';
import { inferSearchSource, trackFlightSearchSubmitted } from '../lib/flightSearchAnalytics.js';

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function renderResults(page, data) {
  page.innerHTML = '';

  const flights = data.cheapest_flights || [];
  const isEmpty = flights.length === 0;
  const deeplink = data.base_deeplink;

  // Cheapest section
  const cheapest = document.createElement('section');
  cheapest.className = 'flight-results-section';
  cheapest.id = 'cheapest-flights-grid';
  cheapest.innerHTML = `
    <p class="flight-results-section__eyebrow">${isEmpty ? 'No matches' : 'Sorted by price'}</p>
    <h2 class="flight-results-section__title">${isEmpty ? 'No flights found' : 'Cheapest Flights'}</h2>
    ${isEmpty ? `<p class="flight-results-section__lead flight-results-empty-lead">Nothing matched these filters on Getting Away. You can still open the full search on Kiwi.com below.</p>` : ''}
    <div class="flight-results-grid" data-grid></div>
    ${deeplink ? renderDealsCardHTML(deeplink, { empty: isEmpty }) : ''}`;
  const grid = cheapest.querySelector('[data-grid]');
  if (deeplink) {
    grid.appendChild(renderMoreRoutesCard(deeplink));
  }
  flights.forEach((flight, i) => {
    grid.appendChild(renderFlightCard(flight, { id: `flight-card-${i}`, variant: 'grid' }));
  });
  page.appendChild(cheapest);

  if (!isEmpty) {
    renderDestinationBrowser(page, data.all_flights);
  }
}

function groupFlightsByDestination(flights) {
  const grouped = {};
  for (const flight of flights) {
    (grouped[flight.city_to] = grouped[flight.city_to] || []).push(flight);
  }
  return grouped;
}

function renderDestinationBrowser(page, flights) {
  const grouped = groupFlightsByDestination(flights);
  const destinations = Object.entries(grouped)
    .map(([city, cityFlights]) => ({
      city,
      flights: cityFlights,
      count: cityFlights.length,
      fromPrice: Math.min(...cityFlights.map((f) => f.price)),
    }))
    .sort((a, b) => a.fromPrice - b.fromPrice);

  if (!destinations.length) return;

  const section = document.createElement('section');
  section.className = 'flight-results-section';
  section.id = 'browse-by-destination';
  section.innerHTML = `
    <p class="flight-results-section__eyebrow">Full search</p>
    <h2 class="flight-results-section__title">Browse by Destination</h2>
    <p class="flight-results-section__lead">Choose a destination to see every flight we found.</p>
    <div class="flight-results-destinations" data-destinations></div>
    <div class="flight-results-destination-panel" data-destination-panel hidden>
      <button type="button" class="flight-results-destination-panel__back" data-dest-back>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        All destinations
      </button>
      <h3 class="flight-results-section__subtitle" data-dest-title></h3>
      <div class="flight-results-list" data-dest-list></div>
    </div>`;

  const grid = section.querySelector('[data-destinations]');
  const panel = section.querySelector('[data-destination-panel]');
  const titleEl = section.querySelector('[data-dest-title]');
  const listEl = section.querySelector('[data-dest-list]');

  function showDestination(city, cityFlights) {
    grid.hidden = true;
    panel.hidden = false;
    titleEl.textContent = `Flights to ${city}`;
    titleEl.id = `destination-${destinationSlug(city)}`;
    listEl.replaceChildren();
    cityFlights.forEach((flight, i) => {
      listEl.appendChild(renderFlightCard(flight, { id: `${city}-${i}`, variant: 'list' }));
    });
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function hideDestination() {
    panel.hidden = true;
    grid.hidden = false;
  }

  section.querySelector('[data-dest-back]').addEventListener('click', hideDestination);

  for (const { city, flights: cityFlights, count, fromPrice } of destinations) {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'flight-results-dest-card';
    card.innerHTML = `
      <span class="flight-results-dest-card__city">${esc(city)}</span>
      <span class="flight-results-dest-card__meta">${count} flight${count === 1 ? '' : 's'}</span>
      <span class="flight-results-dest-card__price">From £${Math.round(fromPrice)}</span>`;
    card.addEventListener('click', () => showDestination(city, cityFlights));
    grid.appendChild(card);
  }

  page.appendChild(section);
}

function searchParamsFromPage(page) {
  const sp = new URLSearchParams(window.location.search);
  if (sp.get('fly_from') || !page?.dataset.flyFrom) {
    return sp;
  }
  sp.set('fly_from', page.dataset.flyFrom);
  if (page.dataset.tripType) sp.set('tripType', page.dataset.tripType);
  if (page.dataset.adults) sp.set('adults', page.dataset.adults);
  if (page.dataset.children) sp.set('children', page.dataset.children);
  if (page.dataset.infants) sp.set('infants', page.dataset.infants);
  return sp;
}

async function init() {
  const page = document.querySelector('.flight-results-page');
  const sp = searchParamsFromPage(page);

  try {
    if (!page) return;

    if (!sp.get('fly_from')) {
      page.innerHTML = `<p class="flight-results-empty">No search criteria found. <a href="/flight-search" class="flight-results-empty__link">Start a new search</a></p>`;
      return;
    }

    trackFlightSearchSubmitted({
      fly_from: sp.get('fly_from'),
      trip_type: sp.get('tripType'),
      fly_to: sp.get('fly_to'),
      source: inferSearchSource(),
      submit_pathname: window.location.pathname,
      adults: sp.get('adults'),
      children: sp.get('children'),
      infants: sp.get('infants'),
      departure_date: sp.get('departureDate'),
      return_date: sp.get('returnDate'),
    });

    showFlightSearchLoading();
    const data = await searchFlights(sp);
    renderResults(page, data);
  } catch (e) {
    if (page) {
      page.innerHTML = `<div class="flight-results-error">${esc(e.message)}</div>`;
    }
  } finally {
    hideFlightSearchLoading();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
